import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ProductFinishMenu from '@components/ProductSearch/finishMenu';
import ProductTypeMenu from '@components/ProductSearch/typeMenu';
import useIsomorphicLayoutEffect from '@utility/useIsomorphicLayoutEffect';
import bodyScroll from '@helpers/bodyScroll';
import cn from 'classnames';
import * as styles from './styles';
import { useContext } from 'react';
import NavbarContext from '../../context/NavbarContextOld';
import ButtonClose from '@components/ButtonClose';
import Button from '@components/Button';
import { ClearRefinements } from 'react-instantsearch-dom';
import CloseIcon from '../../../static/images/icons/close.svg'


const ProductFilterDropdown = React.forwardRef(
  (
    {
      type,
      label,
      isOpen,
      toggleMenu,
      productFinishes,
      finishCategoriesData,
      attribute,
      attribute2,
      defaultLabel,
      showClearButton,
      isAllSelected
    },
    ref,
  ) => {
    const toggleRef = useRef();
    const dropdownRef = useRef();
    const { navRef, hide, show } = useContext(NavbarContext);

    useIsomorphicLayoutEffect(() => {
      const windowHeight = window.innerHeight;
      const dropdownBottom = dropdownRef.current.getBoundingClientRect().y;
      const dropdownHeight = windowHeight - dropdownBottom;
      dropdownRef.current.style.height = `${isOpen ? dropdownHeight : 0}px`;

      if (isOpen) {
        bodyScroll.lock();
        hide();
        navRef.current.classList.add('!-translate-y-full');
      } else {
        bodyScroll.unlock();
        show();
        navRef.current?.classList.remove('!-translate-y-full');
      }
    }, [isOpen]);

    useEffect(() => {
      return () => {
        bodyScroll.unlock();
        show();
        navRef.current?.classList.remove('!-translate-y-full');
      }
    }, [])

    const renderMenu = () => {
      if (type === 'finish') {
        return (
          <div className="wrapper">
            <ProductFinishMenu
              productFinishes={productFinishes}
              finishCategoriesData={finishCategoriesData}
              isOpen={isOpen}
            />
          </div>
        );
      }

      return (
        <div
          className={cn(styles.filterDropdownTypeMenu, {
            [styles.isActive]: isOpen,
          })}
          style={
            toggleRef.current && {
              marginLeft: toggleRef.current.offsetLeft,
            }
          }
        >
          <ProductTypeMenu
            isOpen={isOpen}
            label={label}
            attribute={attribute}
            defaultLabel={defaultLabel}
          />
        </div>
      );
    };

    const renderButton = () => {
      if (type === 'finish') {
        return (
          <Button
            className={cn(styles.finishPickerButton, 'btn btn-secondary')}
            icon="finish-picker"
            onClick={toggleMenu}
            aria-label="Open Finish Picker"
          >
            Finish Picker
          </Button>
        )
      }

      return (
        <>
        <button
          className={cn(styles.filterDropdownToggle, 'flex items-center', {
            [styles.isActive]: isOpen,
            [styles.filterDropdownHideCaret]: showClearButton && !isAllSelected
          })}
          ref={toggleRef}
          onClick={toggleMenu}
          tabIndex="0"
          key={label}
          type="button"
          aria-label='Clear Refinements'
        >
          <span className={cn(styles.filterTextEllipsis)}>{label}</span>
          {showClearButton && !isAllSelected && (
          <div onClick={(e) => e.stopPropagation()}>
            <ClearRefinements        
              transformItems={items => items.filter(item => (item.attribute === attribute || (attribute2 ? item.attribute === attribute2 : false)))}
              translations={{
                reset: (
                  <span >
                    {/* Add your SVG icon or use an icon library */}
                    <img src={CloseIcon} className='sm-down:w-10'/>
                  </span>
                ),
              }}
              className={styles.clearButton}
            />
          </div>
        )}
        </button>
        
        </>
      )
    }

    return (
      <div ref={ref} className={`${styles.filterDropdown} ${type === 'finish' ? styles.finishPickerWrapper : 'md:mb-6'}`}>
        {renderButton()}
        <div
          ref={dropdownRef}
          className={cn(styles.filterDropdownContainer, {
            [styles.filterDropdownContainerOpen]: isOpen,
          })}
        >
          {type === 'finish' && <div className={styles.finishCloseButton}>
            <ButtonClose
              screenReader="Close finish picker"
              white
              onClick={toggleMenu}
              aria-label="Close finish picker"
            />
          </div>}
          {renderMenu()}
        </div>
      </div>
    );
  },
);

ProductFilterDropdown.displayName = 'ProductFilterDropdown';

ProductFilterDropdown.propTypes = {
  type: PropTypes.oneOf(['type', 'finish']),
  label: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  toggleMenu: PropTypes.func.isRequired,
  productFinishes: PropTypes.array,
  finishCategoriesData: PropTypes.array,
  attribute: PropTypes.string,
  defaultLabel: PropTypes.string,
  showClearButton: PropTypes.bool,
  isAllSelected: PropTypes.bool
};

ProductFilterDropdown.defaultProps = {
  type: 'type',
  isOpen: false,
  productFinishes: null,
  finishCategoriesData: null,
  attribute: '',
  defaultLabel: '',
  showClearButton: false,
  isAllSelected: false
};

export default ProductFilterDropdown;
